import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Plus, X, UserPlus } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

export default function ProjectTeamManager({ projectId, projectName, onClose }) {
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedRole, setSelectedRole] = useState("member");
  const queryClient = useQueryClient();

  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: () => base44.entities.User.list(),
  });

  const { data: teams = [] } = useQuery({
    queryKey: ["project-teams", projectId],
    queryFn: () => base44.entities.Team.filter({ related_to_type: "project", related_to_id: projectId }),
    enabled: !!projectId,
  });

  const { data: teamMembers = [] } = useQuery({
    queryKey: ["project-team-members", projectId],
    queryFn: async () => {
      if (teams.length === 0) return [];
      const allMembers = await base44.entities.TeamMember.list();
      const teamIds = teams.map(t => t.id);
      return allMembers.filter(m => teamIds.includes(m.team_id));
    },
    enabled: teams.length > 0,
  });

  const createTeamMutation = useMutation({
    mutationFn: async (data) => {
      let team = teams[0];
      if (!team) {
        team = await base44.entities.Team.create({
          name: `${projectName} Team`,
          related_to_type: "project",
          related_to_id: projectId,
          related_to_name: projectName,
          status: "active",
        });
      }
      return team;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-teams"] });
    },
  });

  const addMemberMutation = useMutation({
    mutationFn: async ({ userId, role }) => {
      let team = teams[0];
      if (!team) {
        team = await createTeamMutation.mutateAsync();
      }
      const user = users.find(u => u.id === userId);
      return base44.entities.TeamMember.create({
        team_id: team.id,
        team_name: team.name,
        user_email: user.email,
        user_name: user.full_name,
        role: role,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-team-members"] });
      setSelectedUserId("");
      toast.success("Medlem lagt til");
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: (memberId) => base44.entities.TeamMember.delete(memberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-team-members"] });
      toast.success("Medlem fjernet");
    },
  });

  const handleAddMember = () => {
    if (!selectedUserId) return;
    addMemberMutation.mutate({ userId: selectedUserId, role: selectedRole });
  };

  const availableUsers = users.filter(
    u => !teamMembers.some(m => m.user_email === u.email)
  );

  const roleLabels = {
    leader: "Teamleder",
    member: "Medlem",
    contributor: "Bidragsyter",
  };

  const roleColors = {
    leader: "bg-lime-100 text-zinc-900",
    member: "bg-blue-100 text-blue-700",
    contributor: "bg-slate-100 text-slate-700",
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-lime-100 to-lime-200 flex items-center justify-center">
              <Users className="w-5 h-5 text-zinc-900" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Prosjektteam</h2>
              <p className="text-sm text-slate-500">{projectName}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Add Member Section */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-900">Legg til medlem</h3>
            <div className="flex gap-3">
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Velg bruker" />
                </SelectTrigger>
                <SelectContent>
                  {availableUsers.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.full_name} ({user.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="leader">Teamleder</SelectItem>
                  <SelectItem value="member">Medlem</SelectItem>
                  <SelectItem value="contributor">Bidragsyter</SelectItem>
                </SelectContent>
              </Select>
              <Button 
                onClick={handleAddMember}
                disabled={!selectedUserId || addMemberMutation.isPending}
                className="bg-zinc-900 hover:bg-zinc-900"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Legg til
              </Button>
            </div>
          </div>

          {/* Team Members List */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-900">
              Teammedlemmer ({teamMembers.length})
            </h3>
            {teamMembers.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Users className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">Ingen teammedlemmer ennå</p>
              </div>
            ) : (
              <div className="space-y-2">
                {teamMembers.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-8 h-8 rounded-full bg-lime-100 flex items-center justify-center">
                        <span className="text-xs font-semibold text-zinc-900">
                          {member.user_name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">{member.user_name}</p>
                        <p className="text-xs text-slate-500">{member.user_email}</p>
                      </div>
                      <Badge className={`${roleColors[member.role]} border-0`}>
                        {roleLabels[member.role]}
                      </Badge>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        if (confirm("Er du sikker på at du vil fjerne dette medlemmet?")) {
                          removeMemberMutation.mutate(member.id);
                        }
                      }}
                      className="text-slate-400 hover:text-rose-600"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}